import {
  rmaNavigationState,
  useRmaStore,
  checkHaveFacilitySubscribeProtectPlus,
} from '@/entities/rma';
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { scrollTop, auth, GETTEXT_ALL_FACILITIES } from '@/shared/lib';
import { useMergeState, useUpdateEffect, useElementChange } from '@/shared/lib/customHooks';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from 'ttag';

/**
 * Backs the RMA fleet page: restores the active tab, decides which tabs are available and exposes the facility and ticket-count data the tabs need. The waiting-for-return tab is hidden when every or the selected facility has Test status, switching away from it, routing with isMaintenanceTab opens the maintenance tab, the header's is-scrolling and has-login-banner classes follow scroll and layout changes, and the ticket counts are refetched whenever the selected facility changes.
 *
 * @returns Tab state, the header ref, facility data and the tab switcher.
 */
const useRma = () => {
  const { state: locationState } = useLocation();

  /**
   * List of facilities the signed-in user supports, taken from the support store. Subscribes to the store's supportFacilities field, which stays unset until loaded and therefore also drives the page reloading flag.
   *
   * @returns The support facility list.
   */
  const supportFacilities = useSupportStore((s) => s.supportFacilities);
  /**
   * Currently selected facility taken from the user store. Subscribes to the store's selectedFacility field so tab visibility and ticket-count refreshes follow facility changes.
   *
   * @returns The selected facility option.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);
  /**
   * RMA ticket counts taken from the RMA store. Subscribes to the store's rmaTicketCount field so tab counters update after a refetch.
   *
   * @returns The current RMA ticket counts.
   */
  const rmaTicketCountRedux = useRmaStore((s) => s.rmaTicketCount);

  /**
   * RMA store action that fetches ticket counts for a facility. Read from useRmaStore as its fetchRmaTicketCount member so the page can refresh counts on facility change.
   *
   * @returns The fetchRmaTicketCount action.
   */
  const getRmaTicketCountAction = useRmaStore((s) => s.fetchRmaTicketCount);

  const [state, setState] = useMergeState({
    activeTab: locationState?.isMaintenanceTab ? '3' : rmaNavigationState.tab || '1',
  });

  const headerRef = useRef<HTMLDivElement>(null);

  const isChangePosition = useElementChange(headerRef);

  /**
   * Determines whether the Waiting For Return tab must be hidden for the current facility selection, memoized on the support facility list and the selected facility. Yields false when there are no support facilities or when All Facilities is selected, and true when every facility or the matched selected facility has status 'Test'.
   *
   * @returns True when the Waiting For Return tab should be hidden.
   */
  const isHideWaitingForReturnTab = React.useMemo(() => {
    if (_.isEmpty(supportFacilities)) {
      return false;
    }

    if (_.every(supportFacilities, { status: 'Test' })) {
      return true;
    }

    if (selectedFacilityRedux?.value === GETTEXT_ALL_FACILITIES) {
      return false;
    }

    const findedSelectedFacility = _.find(supportFacilities, { id: selectedFacilityRedux.value });
    if (findedSelectedFacility) {
      if (findedSelectedFacility.status === 'Test') {
        return true;
      }
    }

    return false;
  }, [supportFacilities, selectedFacilityRedux]);

  /**
   * Whether the selected facility subscribes to the Protect Plus plan. Derived by passing the selected facility and the support facility list to checkHaveFacilitySubscribeProtectPlus.
   *
   * @returns Protect Plus subscription flag for the selected facility.
   */
  const isFacilitySubscribeProtectPlus = checkHaveFacilitySubscribeProtectPlus(
    selectedFacilityRedux,
    supportFacilities,
  );
  const isReloading = !supportFacilities;

  /**
   * Remembers the RMA page's active tab outside React state. The tab is assigned onto the module-level rmaNavigationState so returning to the page restores it.
   *
   * @param tab - Key of the tab to remember.
   * @returns Nothing.
   */
  const updateTabGoto = (tab: any) => {
    _.assign(rmaNavigationState, { tab });
  };

  /**
   * Switches the RMA page to another tab. Nothing happens when the tab is already active, otherwise it is recorded in the shared navigation state and stored as the active tab.
   *
   * @param tab - Key of the tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: any) => {
    if (tab !== state.activeTab) {
      updateTabGoto(tab);
      setState({ activeTab: tab });
    }
  };

  /**
   * Keeps the RMA page header in sync with the window scroll position. The is-scrolling class is added once the page is scrolled away from the top and removed again when it returns to zero.
   *
   * @returns Nothing.
   */
  const handleScroll = () => {
    if (headerRef.current) {
      const { classList } = headerRef.current;
      if (window.scrollY === 0 && classList.contains('is-scrolling')) {
        headerRef.current.classList.remove('is-scrolling');
      } else if (window.scrollY !== 0 && !classList.contains('is-scrolling')) {
        headerRef.current.classList.add('is-scrolling');
      }
    }
  };

  useEffect(() => {
    scrollTop();
    document.title = t`Biocare Diagnostics - Fleet`;
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (locationState?.isMaintenanceTab) {
      locationState.isMaintenanceTab = false;
      setState({ activeTab: '3' });
      return;
    }
    if (isHideWaitingForReturnTab && state.activeTab === '1') {
      setState({ activeTab: '2' });
    }
  }, [isHideWaitingForReturnTab, state.activeTab, locationState?.isMaintenanceTab]);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  useUpdateEffect(() => {
    getRmaTicketCountAction(selectedFacilityRedux.value);
  }, [selectedFacilityRedux]);

  return {
    state,
    headerRef,
    supportFacilities,
    selectedFacilityRedux,
    rmaTicketCountRedux,
    isHideWaitingForReturnTab,
    isFacilitySubscribeProtectPlus,
    isReloading,
    navigateTab,
  };
};

export { useRma };
